import React from "react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { FOOTER } from "../../constants/testIds";
import { CONTACT, BOOKING_URL, GBP_URL, INSTAGRAM_URL, FACEBOOK_URL } from "../../lib/site";
import { SITE } from "../../lib/seo";
import { Instagram, Facebook } from "lucide-react";

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.root}
      className="bg-forest text-paper paper-grain"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Logo invert size="md" />
            <p className="mt-6 font-display italic text-2xl leading-snug max-w-md">
              Where calm meets competence.
            </p>
            <p className="mt-6 text-sm leading-relaxed text-paper/70 max-w-md">
              A new dental home for Vaughan, where the quality of your care and
              the way you experience it matter equally.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="font-meta text-paper/60 mb-5">Visit</div>
            <p className="text-sm leading-relaxed text-paper/80">
              {CONTACT.addressLines[0]}<br />
              {CONTACT.addressLines[1]}
            </p>
            <a
              href={`tel:${CONTACT.phoneTel}`}
              className="block text-sm mt-3 text-paper/80 hover:text-linen transition-colors"
            >
              {CONTACT.phoneLabel}
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              className="block text-sm text-paper/80 hover:text-linen transition-colors"
            >
              {CONTACT.email}
            </a>

            <div className="flex items-center gap-4 mt-6">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Alae Dental on Instagram"
                className="text-paper/70 hover:text-linen transition-colors"
              >
                <Instagram size={20} strokeWidth={1.6} />
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Alae Dental on Facebook"
                className="text-paper/70 hover:text-linen transition-colors"
              >
                <Facebook size={20} strokeWidth={1.6} />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="font-meta text-paper/60 mb-5">Explore</div>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/about" className="hover:text-linen transition-colors">
                  About
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-linen transition-colors">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-linen transition-colors">
                  Journal
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-linen transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-linen transition-colors"
                >
                  Book Online
                </a>
              </li>
              <li>
                <a
                  href={GBP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-linen transition-colors"
                >
                  Google Reviews
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="font-meta text-paper/60 mb-5">Hours</div>
            <ul className="space-y-2 text-sm text-paper/80">
              {CONTACT.hours.map((h) => (
                <li key={h.days}>
                  <span className="block leading-tight">{h.days}</span>
                  <span className="text-paper/60">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-paper/15 flex flex-col md:flex-row justify-between gap-4 text-xs text-paper/60">
          <p data-testid={FOOTER.copyright}>
            © {new Date().getFullYear()} Alae Dental. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 md:justify-end">
            <span data-testid="footer-last-updated" className="text-paper/60">
              Last updated: {SITE.lastUpdatedLabel}
            </span>
            <Link to="/privacy" data-testid={FOOTER.privacy} className="text-paper/80 hover:text-linen transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid={FOOTER.terms} className="text-paper/80 hover:text-linen transition-colors">
              Terms of Use
            </Link>
            <span>Woodbridge · Vaughan · GTA</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
